<template>
    <div class="substance-field-display-edit">
        <div v-if="readonly">
            <div>{{ noSubstance ? noSubstanceLabel : (displayName || '-') }}</div>
            <div v-if="meta" class="text-muted-dark small">{{ meta }}</div>
        </div>
        <div v-else>
            <div v-if="showSelector">
                <SubstanceSelect
                    ref="selector"
                    :value="value"
                    :search-fn="searchFn"
                    :placeholder="placeholder"
                    :start-typing-text="startTypingText"
                    :no-options-text="noOptionsText"
                    :clearable="true"
                    :append-to-body="true"
                    :include-no-substance="allowNoSubstance"
                    :no-substance-label="noSubstanceLabel"
                    @input="onSelect"
                    @close="onSelectorClosed"
                    @search-blur="onSelectorClosed"
                >
                    <template #option="option">
                        <div>
                            <div>{{ option.name || option.title }}</div>
                            <div class="text-muted-dark small" v-if="option.cas || option.ec">
                                <span v-if="option.cas">CAS: {{ option.cas }}</span>
                                <span v-if="option.cas && option.ec"> · </span>
                                <span v-if="option.ec">EC: {{ option.ec }}</span>
                            </div>
                            <b-badge
                                v-if="isSubstanceSelected(group, option)"
                                variant="success"
                                class="mt-1 d-inline-block"
                            >
                                selected
                            </b-badge>
                        </div>
                    </template>
                    <template #selected-option="option">
                        <span class="substance-selected-text" :title="getSelectedTitle(option)">
                            {{ option.name || option.title }}
                        </span>
                    </template>
                    <template #no-options="{search}">
                        <b-button
                            v-if="search && search.length >= 2"
                            size="sm"
                            variant="outline-secondary"
                            @click="$emit('request-add-substance', search)"
                        >
                            {{ requestAddText }}
                        </b-button>
                    </template>
                </SubstanceSelect>
            </div>
            <div v-else>
                <button
                    type="button"
                    class="substance-link"
                    :class="{ 'text-muted': noSubstance }"
                    @click="openSelector"
                >
                    {{ noSubstance ? noSubstanceLabel : displayName }}
                </button>
                <div v-if="meta" class="text-muted-dark small">{{ meta }}</div>
            </div>
            <div v-if="touched && error" class="text-danger small">
                {{ error }}
            </div>
        </div>
    </div>
</template>

<script>
import { BBadge, BButton } from 'bootstrap-vue'
import SubstanceSelect from '@/views/product/components/product-card/fmd/common/SubstanceSelect.vue'

export default {
    name: 'SubstanceFieldDisplayEdit',
    components: {
        BBadge,
        BButton,
        SubstanceSelect,
    },
    props: {
        value: {
            type: Object,
            default: null,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        allowNoSubstance: {
            type: Boolean,
            default: false,
        },
        noSubstance: {
            type: Boolean,
            default: false,
        },
        noSubstanceLabel: {
            type: String,
            default: '',
        },
        displayName: {
            type: String,
            default: '',
        },
        touched: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: '',
        },
        searchFn: {
            type: Function,
            required: true,
        },
        placeholder: {
            type: String,
            default: '',
        },
        startTypingText: {
            type: String,
            default: '',
        },
        noOptionsText: {
            type: String,
            default: '',
        },
        requestAddText: {
            type: String,
            default: '',
        },
        meta: {
            type: String,
            default: '',
        },
        group: {
            type: Object,
            required: true,
        },
        isSubstanceSelected: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            showSelector: !this.value && !this.noSubstance,
        }
    },
    watch: {
        value(next) {
            if (!next) {
                this.showSelector = !this.noSubstance
            }
        },
        noSubstance(next) {
            if (next) {
                this.showSelector = false
                return
            }
            if (!this.value) {
                this.showSelector = true
            }
        },
    },
    methods: {
        openSelector() {
            this.showSelector = true
            this.$nextTick(() => {
                const selector = this.$refs.selector
                if (selector && typeof selector.focusSearchInput === 'function') {
                    selector.focusSearchInput()
                }
            })
        },
        closeSelector() {
            if (!this.value && !this.noSubstance) {
                return
            }
            this.showSelector = false
        },
        onSelectorClosed() {
            this.closeSelector()
        },
        onSelect(option) {
            this.$emit('input', option)
            if (option) {
                this.showSelector = false
            }
        },
        getSelectedTitle(option) {
            const name = option.name || option.title || ''
            const extra = []
            if (option.cas) {
                extra.push(`CAS: ${option.cas}`)
            }
            if (option.ec) {
                extra.push(`EC: ${option.ec}`)
            }
            return extra.length ? `${name} (${extra.join(', ')})` : name
        },
    },
}
</script>

<style scoped>
.substance-field-display-edit :deep(.vs__selected-options) {
    flex-wrap: nowrap;
    overflow: hidden;
}
.substance-field-display-edit :deep(.vs__selected) {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
}
.substance-selected-text {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.substance-link {
    background: transparent;
    border: 0;
    padding: 0;
    color: #2b6cb0;
    text-decoration: underline;
    cursor: pointer;
    text-align: left;
}
.substance-link:hover {
    color: #1e4e8c;
}
.text-muted-dark {
    color: #6c757d;
}
</style>
